import { Info } from 'lucide-react'
import { useState } from 'react'

import { DriverDetailsDialog } from '@/components/DriverDetailsDialog'
import { EquipmentDetailsDialog } from '@/components/EquipmentDetailsDialog'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useDriver } from '@/models/driver'
import { useTrailer, useTruck } from '@/models/equipment'
import type { FileKind } from '@/models/vault'
import { useVault } from './VaultContext'

export type RecordKind = Exclude<FileKind, 'carrier'>

/** A small (i): the roster record behind a driver's or vehicle's file, in the dialog the roster itself uses. */
export function RecordInfoButton({
  kind,
  entityId,
  name,
  className,
}: {
  kind: RecordKind
  entityId: string
  name: string
  className?: string
}) {
  const { orgId } = useVault()
  const [open, setOpen] = useState(false)
  // One of three fetches, and only once asked for — hooks cannot be picked at runtime.
  const driver = useDriver(orgId, entityId, open && kind === 'drivers')
  const truck = useTruck(orgId, entityId, open && kind === 'trucks')
  const trailer = useTrailer(orgId, entityId, open && kind === 'trailers')
  const isLoading = open && (driver.isLoading || truck.isLoading || trailer.isLoading)

  return (
    <>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={`About ${name}`}
        title="Record details"
        className={className}
        onClick={() => setOpen(true)}
      >
        {isLoading ? <Spinner className="size-4" /> : <Info className="text-muted-foreground size-4" />}
      </Button>
      {kind === 'drivers' && driver.data && (
        <DriverDetailsDialog driver={driver.data} open={open} onOpenChange={setOpen} />
      )}
      {kind === 'trucks' && truck.data && (
        <EquipmentDetailsDialog item={truck.data} label="Truck" open={open} onOpenChange={setOpen} />
      )}
      {kind === 'trailers' && trailer.data && (
        <EquipmentDetailsDialog item={trailer.data} label="Trailer" open={open} onOpenChange={setOpen} />
      )}
    </>
  )
}
